import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, Form, Button, Row, Col } from "react-bootstrap";
import useGetAccount from "../../../../Admin/Teams/common/api/useGetAccount";
import FormTextField from "@components/Forms/FormTextField";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { RootState } from "@reducers/rootReducer";
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  V1UserResource,
  useWhoAmIQuery,
} from "@generated";
import { User, Option } from "./types";
import "../style.css";
import { MultiSelectAutocomplete } from "@components/FormsV2/MultiSelectAutoComplete";

interface UserInfoProps {
  mode: "create" | "edit";
  user: User;
  setUserAttributes: (user) => void;
  refetch?: () => void;
  onSubmit: (nextAction: "edit" | "new") => void;
  roles: Option[];
  team: Option[];
  isArchived: boolean;
}

const UserInfo: React.FC<UserInfoProps> = ({
  mode,
  user,
  setUserAttributes,
  onSubmit,
  roles,
  team,
  refetch = () => {},
  isArchived,
}) => {
  const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: whoAmIData } = useWhoAmIQuery();
  const currentUserId = whoAmIData?.data?.id;
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { account } = useGetAccount();

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();

  type UserType = "view_only" | "licensed" | "sales";
  const isValidUserType = (type: any): type is UserType => {
    return ["view_only", "licensed", "sales"].includes(type);
  };

  const isDirty = useMemo(() => {
    if (!user.originalAttributes || !user.originalRelationships) return true;

    return (
      user.attributes.name !== user.originalAttributes.name ||
      user.attributes.email !== user.originalAttributes.email ||
      user.attributes.phone !== user.originalAttributes.phone ||
      user.attributes.title !== user.originalAttributes.title ||
      user.attributes.userType !== user.originalAttributes.userType ||
      JSON.stringify(user.attributes.roles) !==
        JSON.stringify(user.originalAttributes.roles) ||
      JSON.stringify(user.relationships.teams?.data) !==
        JSON.stringify(user.originalRelationships.teams?.data)
    );
  }, [user]);

  const isSaveDisabled = useMemo(() => {
    return loading || (mode === "edit" && isArchived && !isDirty);
  }, [loading, mode, isArchived, isDirty]);

  const formatUserData = (data: User): V1UserResource => {
    const safeUserType = isValidUserType(data.attributes.userType)
      ? data.attributes.userType
      : undefined;
    return {
      id: Number(data.id),
      type: "users",
      attributes: {
        active: data.attributes.active,
        name: data.attributes.name,
        phone: data.attributes?.phone || "",
        email: data.attributes?.email || "",
        title: data.attributes?.title || "",
        roles: data.attributes?.roles || "",
        ...(safeUserType && { "user-type": safeUserType }),
      },
      relationships: {
        teams: {
          data:
            data.relationships?.teams?.data?.map((t) => ({
              id: Number(t.id),
              type: "teams",
            })) ?? [],
        },
        ...(data.attributes?.roles?.length > 0 && {
          roles: {
            data: data.attributes.roles.map((r) => ({
              id: r.id,
              type: "roles",
            })),
          },
        }),
      },
    };
  };

  const selectedTeams = useMemo(() => {
    if (!user.relationships?.teams?.data || team.length === 0) return [];

    return user.relationships.teams.data.map((rel) => {
      const match = team.find((t) => t.value === String(rel.id));
      return match || { label: "", value: String(rel.id) };
    });
  }, [user.relationships?.teams?.data, team]);

  const handleFailResponse = (msg: string) => {
    setLoading(false);
    setErrorMessage(msg || "Something went wrong.");
    setShowFailAlert(true);
  };

  const handleUpdateUser = () => {
    setLoading(true);

    if (!account?.id) {
      handleFailResponse("Account must exist to create a product.");
      return;
    }

    const data = formatUserData(user);

    updateUser({ slug: accountSlug, id: Number(data.id), body: { data } })
      .unwrap()
      .then((res) => {
        const id = res?.data?.id;
        const updatedUser = res?.data;
        if (updatedUser) {
          setUserAttributes((user) => ({
            ...user,
            attributes: {
              ...user.attributes,
              active:
                isArchived && isDirty ? true : user.attributes?.active ?? true,
              ...updatedUser.attributes,
            },
          }));
          setSuccessMessage("User updated successfully!");
          setShowSuccessAlert(true);
          setTimeout(() => {
            refetch();
          }, 200);
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        console.error("Error details:", JSON.stringify(error?.data, null, 2));

        handleFailResponse(
          error?.data?.errors?.[0]?.detail || "An error occured."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSaveUser = (nextAction: "edit" | "new") => {
    setLoading(true);

    if (!account?.id) {
      handleFailResponse("Account must exist to create a user.");
      return;
    }

    const { name, email, phone, title, roles } = user.attributes;

    const validTeamIds = new Set(team.map((t) => Number(t.value)));

    const teamData = user.relationships.teams.data.filter((t) =>
      validTeamIds.has(Number(t.id))
    );

    if (!name.trim() || !email.trim()) {
      setErrorMessage("Name and Email are required.");
      setShowFailAlert(true);
      setLoading(false);
      return;
    }

    const newUserData: V1UserResource = {
      type: "users",
      attributes: {
        name: user.attributes.name,
        email: user.attributes.email,
        ...(user.attributes.phone && { phone: user.attributes.phone }),
        ...(user.attributes.title && { title: user.attributes.title }),
        "view-only": false,
      },
      relationships: {
        account: {
          data: {
            id: account.id,
            type: "accounts",
          },
        },
        ...(user.relationships.teams?.data?.length > 0 && {
          teams: {
            data: user.relationships.teams.data.map((t) => ({
              id: Number(t.id),
              type: "teams",
            })),
          },
        }),
        ...(user.attributes.roles.length > 0 && {
          roles: {
            data: user.attributes.roles.map((r) => ({
              id: r.id,
              type: "roles",
            })),
          },
        }),
      },
    };

    createUser({ slug: accountSlug, body: { data: newUserData } })
      .unwrap()
      .then((res) => {
        const newUserId = res?.data?.id;
        if (newUserId) {
          if (nextAction === "edit") {
            navigate(`/users/${currentUserId}/edit/edit/${newUserId}`, {
              state: { newlyCreatedUser: res?.data },
            });
          } else if (nextAction === "new") {
            setUserAttributes({
              id: 0,
              type: "vendors",
              attributes: {
                name: "",
                phone: "",
                email: "",
                title: "",
                roles: [],
              },
              relationships: {
                teams: { data: [] },
              },
            });
          }
        } else {
          handleFailResponse("Failed to create vendor.");
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        handleFailResponse(
          error?.data?.errors?.[0]?.detail || "An error occurred."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderUserForm = () => (
    <Form>
      <Card.Title
        style={{ color: "#1c2655", padding: "8px 0px", fontWeight: 700 }}
      >
        User Contact
      </Card.Title>
      <Row>
        {(["name", "email"] as const).map((key) => (
          <Col key={key}>
            <FormTextField
              id={key}
              label={key.charAt(0).toUpperCase() + key.slice(1)}
              value={user.attributes[key] || ""}
              placeholder={`Enter ${key}`}
              onChange={(e) =>
                setUserAttributes({
                  ...user,
                  attributes: {
                    ...user.attributes,
                    [key]: e.target.value,
                  },
                })
              }
              required={false}
              readOnly={isArchived}
            />
          </Col>
        ))}
      </Row>
      <Row>
        {(["phone", "title"] as const).map((key) => (
          <Col key={key}>
            <FormTextField
              id={key}
              label={key.charAt(0).toUpperCase() + key.slice(1)}
              value={user.attributes[key] || ""}
              placeholder={`Enter ${key}`}
              onChange={(e) =>
                setUserAttributes({
                  ...user,
                  attributes: {
                    ...user.attributes,
                    [key]: e.target.value,
                  },
                })
              }
              required={false}
              readOnly={isArchived}
            />
          </Col>
        ))}
      </Row>
      <hr />
      <Card.Title
        style={{ color: "#1c2655", padding: "8px 0px", fontWeight: 700 }}
      >
        User Type, Teams, and Roles
      </Card.Title>
      <Row mb={3} label="User Type">
        <Col md={6}>
          <div
            className={`userTypeBox ${
              user.attributes.userType === "licensed" ? "selected" : ""
            }`}
            onClick={() => {
              if (isArchived) return;
              setUserAttributes({
                ...user,
                attributes: {
                  ...user.attributes,
                  userType: "licensed",
                },
              });
            }}
          >
            <div className="userTypeContext">
              <span className="userTypeIndicator">
                <div className="userTypeSelection">
                  {user.attributes.userType === "licensed" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <circle cx="8" cy="8" r="7.5" fill="#418172" />
                      <circle
                        cx="8"
                        cy="8"
                        r="7.5"
                        stroke="white"
                        strokeOpacity="0.2"
                      />
                      <circle cx="8" cy="8" r="7.5" stroke="#E3E4E5" />
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        stroke="white"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <circle
                        cx="8"
                        cy="8"
                        r="7.5"
                        stroke="white"
                        strokeOpacity="0.2"
                      />
                      <circle cx="8" cy="8" r="7.5" stroke="#E3E4E5" />
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        stroke="white"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                  <div>
                    <div className="userTypeLabel">Licensed</div>
                    <div className="userTypeSubtext">
                      A paid user with full edit access.
                    </div>
                  </div>
                </div>
              </span>
            </div>
          </div>
        </Col>
        <Col md={6}>
          <div
            className={`userTypeBox ${
              user.attributes.userType === "view_only" ? "selected" : ""
            }`}
            onClick={() =>
              setUserAttributes({
                ...user,
                attributes: {
                  ...user.attributes,
                  userType: "view_only",
                },
              })
            }
          >
            <div className="userTypeContext">
              <span className="userTypeIndicator">
                <div className="userTypeSelection">
                  {user.attributes.userType === "view_only" ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <circle cx="8" cy="8" r="7.5" fill="#418172" />
                      <circle
                        cx="8"
                        cy="8"
                        r="7.5"
                        stroke="white"
                        strokeOpacity="0.2"
                      />
                      <circle cx="8" cy="8" r="7.5" stroke="#E3E4E5" />
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        stroke="white"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <circle
                        cx="8"
                        cy="8"
                        r="7.5"
                        stroke="white"
                        strokeOpacity="0.2"
                      />
                      <circle cx="8" cy="8" r="7.5" stroke="#E3E4E5" />
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        stroke="white"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                  <div>
                    <div className="userTypeLabel">View-Only</div>
                    <div className="userTypeSubtext">
                      A free user with the ability to review and approve.
                    </div>
                  </div>
                </div>
              </span>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <MultiSelectAutocomplete
            label="Teams"
            id="edit-user-teams"
            options={team}
            value={selectedTeams}
            // isDisabled={isArchived}
            onChange={(selected) => {
              const selectedArray = Array.isArray(selected) ? selected : [];
              setUserAttributes({
                ...user,
                relationships: {
                  ...user.relationships,
                  teams: {
                    data: selectedArray.map((item) => ({
                      id: Number(item.value),
                      type: "teams",
                    })),
                  },
                },
              });
            }}
          />
        </Col>
        <Col md={6}>
          <MultiSelectAutocomplete
            label="Roles"
            id="edit-user-roles"
            options={roles}
            value={
              user.attributes.roles?.map((role) => {
                const match = roles.find((r) => r.value === String(role.id));
                return match || { label: "", value: String(role.id) };
              }) || []
            }
            // isDisabled={isArchived}
            onChange={(selected) => {
              const selectedArray = Array.isArray(selected) ? selected : [];
              setUserAttributes({
                ...user,
                attributes: {
                  ...user.attributes,
                  roles: selectedArray.map((item) => ({
                    id: Number(item.value),
                    name: item.label,
                  })),
                },
              });
            }}
          />
        </Col>
      </Row>
    </Form>
  );

  return (
    <>
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <Card
        style={{
          padding: "16px",
          borderRadius: "4px",
          border: "1px solid var(--gray-3-stroke, #D7D7D7)",
          background: "var(--white, #FFF)",
          boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Card.Header
          style={{
            backgroundColor: "#fff",
            lineHeight: "40px",
            display: "block",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              className="userAccordianTitle"
              style={{ height: "40px", margin: "0", alignContent: "center" }}
            >
              <Button
                className="userCancelButton"
                variant="secondary"
                onClick={() =>
                  window.location.replace(
                    `${appHost}/users/${currentUserId}/edit`
                  )
                }
              >
                Cancel
              </Button>
              {mode === "edit" ? `Edit ${user.attributes.name}` : "Add a User"}
            </div>

            <div className="userActionBtns">
              {mode === "create" && (
                <Button
                  className="userCancelButton"
                  onClick={() => handleSaveUser("new")}
                  disabled={loading}
                  variant="secondary"
                >
                  {loading ? <SmallSpinner /> : "Save and Add Another"}
                </Button>
              )}

              <Button
                className="userRegButton"
                onClick={() =>
                  mode === "edit" ? handleUpdateUser() : handleSaveUser("edit")
                }
                disabled={isSaveDisabled}
                variant="primary"
              >
                {loading ? <SmallSpinner /> : "Save"}
              </Button>
            </div>
          </div>
          <hr />
        </Card.Header>
        <Card.Body style={{ padding: "0px 16px 16px 16px" }}>
          {renderUserForm()}
        </Card.Body>
      </Card>
    </>
  );
};

export default UserInfo;
